import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CalendarDays, ArrowRight, MapPin, BadgeCheck } from "lucide-react";
import { IMG } from "@/data/content";
import { useBooking } from "@/lib/booking";
import { scrollToId } from "@/lib/scroll";
import { Img } from "./Reveal";

const lineTransition = (i) => ({ duration: 1, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] });

export function Hero() {
  const { openBooking } = useBooking();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yMain = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const yAlt = useTransform(scrollYProgress, [0, 1], [0, -40]);

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative overflow-hidden grain bg-ivory">
      <div className="pointer-events-none absolute -top-40 -right-40 h-[34rem] w-[34rem] rounded-full bg-teal-brand/10 blur-3xl" />
      <div className="pointer-events-none absolute top-1/2 -left-52 h-[30rem] w-[30rem] rounded-full bg-gold/10 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-5 md:px-8 lg:px-12 pt-32 lg:pt-40 pb-20 lg:pb-28">
        <div className="grid lg:grid-cols-12 gap-14 lg:gap-8 items-center">
          <div className="lg:col-span-6">
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="inline-flex items-center gap-3 rounded-full border border-gold/40 bg-white/70 backdrop-blur px-4 py-2"
            >
              <span className="h-2 w-2 rounded-full bg-lime-brand" />
              <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-pine/80">
                Dental &amp; Skin Solutions
              </span>
            </motion.div>

            <h1 className="mt-7 font-display font-medium tracking-tight text-pine-deep text-5xl md:text-6xl lg:text-7xl leading-[1.04]">
              <span className="masked-line">
                <motion.span initial={{ y: "110%" }} animate={{ y: 0 }} transition={lineTransition(0)}>
                  Your Smile.
                </motion.span>
              </span>
              <span className="masked-line">
                <motion.span initial={{ y: "110%" }} animate={{ y: 0 }} transition={lineTransition(1)}>
                  Your Skin.
                </motion.span>
              </span>
              <span className="masked-line">
                <motion.span initial={{ y: "110%" }} animate={{ y: 0 }} transition={lineTransition(2)} className="italic text-teal-brand">
                  Your Confidence.
                </motion.span>
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="mt-7 max-w-xl text-base md:text-lg leading-relaxed text-slate-600"
            >
              Premium Dental &amp; Skin Care, delivered by experienced doctors through the growing
              Dyntra Clinics network.
            </motion.p>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.9 }}
              className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-pine"
            >
              <MapPin className="h-4 w-4 text-gold" />
              Now serving patients from our Sector 75, Noida clinic.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1 }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <button
                data-testid="hero-book-cta"
                onClick={() => openBooking()}
                className="group inline-flex items-center gap-2.5 rounded-full bg-pine px-7 py-4 text-sm font-bold text-ivory shadow-grand transition-all duration-300 hover:bg-pine-deep hover:-translate-y-0.5"
              >
                <CalendarDays className="h-4.5 w-4.5 h-5 w-5" />
                Book an Appointment
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
              <button
                data-testid="hero-services-cta"
                onClick={() => scrollToId("dental")}
                className="inline-flex items-center gap-2 rounded-full border border-pine/25 bg-white/60 backdrop-blur px-7 py-4 text-sm font-bold text-pine-deep transition-all duration-300 hover:border-gold hover:bg-gold-soft/40"
              >
                Explore Our Services
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 1.2 }}
              className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs font-semibold text-pine/60"
            >
              <span className="inline-flex items-center gap-1.5"><BadgeCheck className="h-4 w-4 text-teal-brand" /> Qualified Doctors</span>
              <span className="inline-flex items-center gap-1.5"><BadgeCheck className="h-4 w-4 text-teal-brand" /> Modern Clinical Setup</span>
              <span className="inline-flex items-center gap-1.5"><BadgeCheck className="h-4 w-4 text-teal-brand" /> Dyntra Store Ecosystem</span>
            </motion.div>
          </div>

          <div className="lg:col-span-6 relative">
            <div className="relative mx-auto max-w-[540px]">
              <motion.div style={{ y: yMain }} className="relative">
                <motion.div
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 1.2, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
                  className="overflow-hidden rounded-t-[11rem] rounded-b-[2rem] shadow-grand ring-1 ring-pine/10"
                >
                  <Img src={IMG.heroMain} alt="Dentist caring for a patient at Dyntra Clinics" className="h-[420px] md:h-[520px] w-full object-cover" />
                </motion.div>
              </motion.div>

              <motion.div
                style={{ y: yAlt }}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 1, delay: 0.7 }}
                className="absolute -right-4 md:-right-10 top-16 w-36 md:w-44 overflow-hidden rounded-2xl shadow-lift ring-4 ring-ivory animate-floaty"
              >
                <Img src={IMG.heroRoom} alt="Modern dental treatment room" className="h-40 md:h-52 w-full object-cover" />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 1, delay: 0.85 }}
                className="absolute -left-4 md:-left-12 bottom-24 w-40 md:w-48 overflow-hidden rounded-2xl shadow-lift ring-4 ring-ivory"
              >
                <Img src={IMG.heroSkin} alt="Skin care consultation" className="h-44 md:h-56 w-full object-cover" />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 1.05 }}
                className="absolute -bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-3 rounded-2xl bg-white/85 backdrop-blur-xl border border-white/40 shadow-grand px-5 py-4"
                data-testid="hero-trust-badge"
              >
                <img src="/dyntra-logo.jpg" alt="" className="h-10 w-10 rounded-full object-cover ring-1 ring-gold/50" />
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-gold">Franchise Clinic</p>
                  <p className="text-sm font-bold text-pine-deep">A Dyntra Store Franchise Clinic</p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
